import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { IoMdClose } from "react-icons/io";
import { useGlobalContext } from "context/globalContext";
import {
  getAutoSuggestAPI,
  createOrUpdateSysPromptsAPI,
} from "Api/sysPromptsAPI";
import PromptFragmentsValueTextArea from "views/admin/tables/prompt-fragments/components/create-or-update-prompt-fragments/components/promptFragmentsValueTextArea/promptFragmentsValueTextArea";
import { consoleLog } from "helpers";

const isFormValid = (formData) => {
  return (
    formData.key?.trim() &&
    formData.name?.trim() &&
    formData.comment?.trim() &&
    formData.value?.trim() &&
    formData.isActive
  );
};

const CreateOrUpdateSysPromptsModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  initialFormData,
  promptFragmentsData,
}) => {
  const { setFetchSysPrompts } = useGlobalContext();
  const {
    sysPromptsFormData,
    setSysPromptsFormData,
    initialSysPromptsFormState,
  } = useGlobalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const [activeSuggestionField, setActiveSuggestionField] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
      if (!sysPromptsFormData?.isActive) {
        setSysPromptsFormData((prev) => ({
          ...prev,
          isActive: "active",
        }));
      }
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen, sysPromptsFormData, setSysPromptsFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSysPromptsFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (
        isEditMode &&
        JSON.stringify(updated) !== JSON.stringify(initialFormData)
      ) {
        setIsChanged(true);
      }

      if (name === "name" && value.trim() === "") {
        updated.key = "";
      }

      return updated;
    });

    if (value?.trim()) {
      setInvalidFields((prev) => ({ ...prev, [name]: false }));
    }

    if (name === "name" && value.trim().length > 2) {
      clearTimeout(debounceTimer);
      const newTimer = setTimeout(() => fetchNameSuggestions(value), 300);
      setDebounceTimer(newTimer);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setSysPromptsFormData((prev) => ({
      ...prev,
      name: suggestion.name,
      key: suggestion.key,
    }));

    setSuggestions([]);
    setActiveSuggestionField("");
  };

  const fetchNameSuggestions = async (query, field) => {
    setLoadingSuggestions(true);
    setActiveSuggestionField(field);

    try {
      const accessJWT = localStorage.getItem("jwtToken");
      const response = await getAutoSuggestAPI(
        query,
        field,
        setLoadingSuggestions,
        accessJWT
      );

      if (Array.isArray(response)) {
        setSuggestions(response);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newInvalidFields = {
      name: !sysPromptsFormData.name?.trim(),
      key: !sysPromptsFormData.key?.trim(),
      comment: !sysPromptsFormData.comment?.trim(),
      value: !sysPromptsFormData.value?.trim(),
      isActive: !sysPromptsFormData.isActive,
    };

    setInvalidFields(newInvalidFields);

    if (Object.values(newInvalidFields).some((invalid) => invalid)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...sysPromptsFormData,
      isActive: sysPromptsFormData.isActive === "active",
    };
    const token = localStorage.getItem("jwtToken");
    const response = await createOrUpdateSysPromptsAPI(
      payload,
      setIsSubmitting,
      token
    );
    consoleLog([response, "response of name"]);

   if (response?.error) {
  toast.error(
    response.message || "Error creating/updating System Prompts. Please try again."
  );
  return;
}

    const successMessage = sysPromptsFormData?.id
      ? "System prompt updated successfully"
      : "System prompt created successfully";
    toast.success(successMessage);

    onSubmit(response.data);
    onClose();
    setSysPromptsFormData(initialSysPromptsFormState);
    setFetchSysPrompts(true);
    setIsSubmitting(false);
  };

  useEffect(() => {
    consoleLog([suggestions]);
  }, [suggestions]);

  const handleCancel = () => {
    setSysPromptsFormData(initialSysPromptsFormState);
    setInvalidFields({});
    onClose();
  };

  const handleFormDataWithTextAreaValue = useCallback(
    (newTextValue) => {
      setSysPromptsFormData((prev) => ({
        ...prev,
        value: newTextValue,
      }));
    },
    [setSysPromptsFormData]
  );

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="relative max-h-[95vh] w-[90%] overflow-auto rounded-xl bg-white shadow-xl lg:w-[75%]">
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
        >
          <IoMdClose size={24} />
        </button>
        <form
          onSubmit={handleSubmit}
          className="h-full w-full p-6 dark:!bg-gray-10 dark:text-white"
        >
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label
                  htmlFor="name"
                  className="mb-2 block font-bold text-gray-700 dark:text-white "
                >
                  <span className="text-red-600">*</span> Name:
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                    autoComplete="off"
                  value={sysPromptsFormData?.name || ""}
                  onChange={handleChange}
                  onInput={(e) => fetchNameSuggestions(e.target.value, "name")}
                  className={`focus:border-transparent w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10 dark:text-white ${
                    invalidFields.name ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isSubmitting || isEditMode}
                  required
                />

                {suggestions.length > 0 && activeSuggestionField === "name" && (
                  <ul className="mt-2 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg">
                    {suggestions.map((s, i) => (
                      <li
                        key={i}
                        onClick={() => handleSuggestionSelect(s)}
                        className="cursor-pointer px-4 py-2 hover:bg-gray-200 dark:!bg-gray-10 dark:text-white "
                      >
                        {s.name}
                      </li>
                    ))}
                  </ul>
                )}

                {loadingSuggestions && activeSuggestionField === "name" && (
                  <p className="mt-1 text-sm text-gray-500">Loading…</p>
                )}
              </div>

              <div className="flex-1">
                <label
                  htmlFor="key"
                  className="mb-2 block font-bold text-gray-700 dark:text-white"
                >
                  <span className="text-red-600">*</span> Key:
                </label>
                <input
                  id="key"
                      autoComplete="off"
                  type="text"
                  onInput={(e) => fetchNameSuggestions(e.target.value, "key")}
                  name="key"
                  value={sysPromptsFormData?.key || ""}
                  onChange={handleChange}
                  className={`focus:border-transparent w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white ${
                    invalidFields.key ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isSubmitting || isEditMode}
                  required
                />
                {suggestions.length > 0 && activeSuggestionField === "key" && (
                  <ul className="mt-2 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg">
                    {suggestions.map((s, i) => (
                      <li
                        className="cursor-pointer px-4 py-2 hover:bg-gray-200 dark:!bg-gray-10 dark:text-white "
                        key={i}
                        onClick={() => handleSuggestionSelect(s)}
                      >
                        {s.key}
                      </li>
                    ))}
                  </ul>
                )}
                {loadingSuggestions && activeSuggestionField === "key" && (
                  <p className="mt-1 text-sm text-gray-500">Loading…</p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block font-bold text-gray-700  dark:text-white ">
                  <span className="text-red-600">*</span> Status:
                </label>
                <div
                  className={`flex items-center ${
                    invalidFields.isActive
                      ? "rounded-md border border-red-500 p-2"
                      : ""
                  }`}
                >
                  <label className="mr-4 dark:text-white ">
                    <input
                      type="radio"
                      name="isActive"
                      value="active"
                      checked={sysPromptsFormData?.isActive === "active"}
                      onChange={handleChange}
                      className="relative top-0.5 mr-1 cursor-pointer"
                      disabled={isSubmitting}
                    />
                    Active
                  </label>
                  <label className="dark:text-white ">
                    <input
                      type="radio"
                      name="isActive"
                      value="inactive"
                      checked={sysPromptsFormData?.isActive === "inactive"}
                      onChange={handleChange}
                      className="relative top-0.5 mr-1 cursor-pointer "
                      disabled={isSubmitting}
                    />
                    In-Active
                  </label>
                </div>
              </div>

              <div className="flex-1">
                <label
                  htmlFor="comment"
                  className="mb-2 block font-bold text-gray-700 dark:text-white"
                >
                  <span className="text-red-600">*</span> Comment:
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={sysPromptsFormData?.comment || ""}
                  onChange={handleChange}
                  rows={3}
                  className={`focus:border-transparent w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10 dark:text-white ${
                    invalidFields.comment ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label
                  htmlFor="value"
                  className="mb-2 block font-bold text-gray-700 dark:text-white"
                >
                  <span className="text-red-600 ">*</span> Value:
                </label>
                <PromptFragmentsValueTextArea
                  className={`focus:border-transparent h-[40vh] max-h-[50vh] w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white ${
                    invalidFields.value ? "border-red-500" : "border-gray-300"
                  }`}
                  handleChange={handleChange}
                  setFormDataWithTextAreaValue={handleFormDataWithTextAreaValue}
                  textAreaValue={sysPromptsFormData?.value || ""}
                  tableData={promptFragmentsData || []}
                />
              </div>
            </div>

            <div className="my-4 mb-6 flex justify-end space-x-4">
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !isFormValid(sysPromptsFormData) ||
                  (isEditMode && !isChanged)
                }
                className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                  isSubmitting ||
                  !isFormValid(sysPromptsFormData) ||
                  (isEditMode && !isChanged)
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-green-600 hover:bg-green-500"
                }`}
              >
                {isSubmitting ? "Submitting…" : isEditMode ? "Update" : "Add"}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrUpdateSysPromptsModal;
